import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { postMultipartFile } from '@/utils/blobApiUtils'
import { normalizeDetailResponse } from '@/utils/apiResponseUtils'
import type {
  INhomHang,
  INhomHangImportRow,
  NhomHangCayNode,
  NhomHangId,
  NhomHangTrangThaiMa,
} from '@/interfaces/nhomHangInterfaces'

const PREFIX = '/nhom-hang'

const toId = (v: unknown): NhomHangId => {
  if (typeof v === 'string' || typeof v === 'number') return v
  return String(v ?? '')
}

const toParentId = (v: unknown): NhomHangId | null => {
  if (v === '' || v == null) return null
  return toId(v)
}

function parseTrangThaiNhom(raw: unknown): NhomHangTrangThaiMa {
  if (raw === 'NGUNG_HOAT_DONG' || raw === 'CHUA_AP_DUNG') return 'NGUNG_HOAT_DONG'
  if (raw === 'DANG_HOAT_DONG' || raw === 'AP_DUNG') return 'DANG_HOAT_DONG'
  if (raw === true) return 'DANG_HOAT_DONG'
  if (raw === false) return 'NGUNG_HOAT_DONG'
  return 'DANG_HOAT_DONG'
}

const toNum = (raw: unknown): number | null => {
  if (raw === '' || raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

function mapCayNode(raw: Record<string, unknown>): NhomHangCayNode {
  const childrenRaw = raw.children
  const children = Array.isArray(childrenRaw)
    ? (childrenRaw as Record<string, unknown>[]).map((c) => mapCayNode(c))
    : undefined
  return {
    id: toId(raw.id),
    maNhom: String(raw.ma ?? raw.maNhom ?? raw.code ?? ''),
    tenNhom: String(raw.tenNhom ?? raw.ten ?? raw.name ?? ''),
    parentId: toParentId(raw.parentId ?? raw.parent_id),
    thuTu: toNum(raw.thuTu) ?? undefined,
    cap: toNum(raw.cap) ?? undefined,
    soLuongHangHoa: toNum(raw.soLuongHangHoa) ?? undefined,
    hasChildren: Boolean(raw.hasChildren ?? children?.length),
    trangThaiMa: parseTrangThaiNhom(raw.trangThai ?? raw.isActive),
    children: children?.length ? children : undefined,
  } as NhomHangCayNode
}

function mapDetail(raw: Record<string, unknown>): INhomHang {
  const ma = parseTrangThaiNhom(raw.trangThai ?? raw.isActive)
  const parent = raw.parent && typeof raw.parent === 'object' ? (raw.parent as Record<string, unknown>) : undefined
  return {
    id: toId(raw.id),
    code: String(raw.ma ?? raw.maNhom ?? raw.code ?? ''),
    name: String(raw.tenNhom ?? raw.ten ?? raw.name ?? ''),
    parentId: toParentId(raw.parentId ?? raw.parent_id),
    parentName:
      raw.tenNhomCha != null
        ? String(raw.tenNhomCha)
        : raw.parentTen != null
          ? String(raw.parentTen)
          : parent?.ten != null
            ? String(parent.ten)
            : parent?.name != null
              ? String(parent.name)
              : null,
    isActive: ma === 'DANG_HOAT_DONG',
    thuTu: toNum(raw.thuTu),
    cap: toNum(raw.cap),
    soLuongHangHoa: toNum(raw.soLuongHangHoa),
    hasChildren: raw.hasChildren == null ? undefined : Boolean(raw.hasChildren),
    createdAt: raw.ngayTao != null ? String(raw.ngayTao) : raw.createdAt != null ? String(raw.createdAt) : undefined,
    updatedAt: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : raw.updatedAt != null ? String(raw.updatedAt) : undefined,
  }
}

function unwrapArray<T>(res: unknown, mapRow: (row: Record<string, unknown>) => T): T[] {
  if (Array.isArray(res)) return (res as Record<string, unknown>[]).map(mapRow)
  const o = res as { data?: unknown; results?: unknown }
  const nested = o?.data as { data?: unknown; results?: unknown } | undefined
  const arr = Array.isArray(o?.data)
    ? o.data
    : Array.isArray(o?.results)
      ? o.results
      : nested && Array.isArray(nested.data)
        ? nested.data
        : nested && Array.isArray(nested.results)
          ? nested.results
          : []
  return (arr as Record<string, unknown>[]).map(mapRow)
}

let cayNhomHangCache: Promise<NhomHangCayNode[]> | null = null

/** Cache theo phiên — cây nhóm hàng ít khi đổi, share giữa các component filter. */
export function getCayNhomHang(options?: { bypassCache?: boolean }): Promise<NhomHangCayNode[]> {
  if (!options?.bypassCache && cayNhomHangCache) return cayNhomHangCache
  cayNhomHangCache = apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-cay-nhom-hang`, {
      params: { page: 1, pageSize: 5000 },
    }).then((res: unknown) => unwrapArray(res, mapCayNode)),
  ).catch((err) => {
    cayNhomHangCache = null
    throw err
  })
  return cayNhomHangCache
}

/**
 * Xoá cache cây nhóm hàng — gọi sau khi `taoNhomHang` / `capNhatNhomHang` /
 * `xoaNhomHang` để lần `getCayNhomHang` kế tiếp buộc fetch lại.
 *
 * Lưu ý: KHÔNG tự động gọi trong API — caller quyết định khi nào cần refresh
 * (vd. sau khi đóng dialog "Tạo nhóm hàng" inline).
 */
export function invalidateCayNhomHangCache(): void {
  cayNhomHangCache = null
}

export function getDanhSachPhangNhomHang(params?: { trangThai?: NhomHangTrangThaiMa }): Promise<INhomHang[]> {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-phang-nhom-hang`, {
      params: params?.trangThai ? { trangThai: params.trangThai } : undefined,
    }).then((res: unknown) => unwrapArray(res, mapDetail)),
  )
}

export function timKiemNhomHang(tuKhoa: string): Promise<INhomHang[]> {
  const t = tuKhoa.trim()
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/tim-kiem-nhom-hang`, { params: { tuKhoa: t || ' ' } }).then((res: unknown) =>
      unwrapArray(res, mapDetail),
    ),
  )
}

export function getChiTietNhomHang(id: NhomHangId) {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-nhom-hang`).then((res: unknown) => {
      const raw = normalizeDetailResponse<Record<string, unknown>>(res as { data?: Record<string, unknown> })
      return { data: mapDetail(raw) }
    }),
  )
}

export function taoNhomHang(
  body: { ten: string; ma?: string; parentId?: NhomHangId | null },
): Promise<{ data: INhomHang }> {
  const ma = body.ma?.trim()
  return apiRequest(
    ApisServiceApp.post(`${PREFIX}/tao-nhom-hang`, {
      ten: body.ten.trim(),
      ...(ma ? { ma } : {}),
      ...(body.parentId != null && body.parentId !== '' ? { parentId: String(body.parentId) } : {}),
    }),
  ).then((res: unknown) => {
    // Backend trả `{ statusCode, message, data: DMNhomHang }` qua TransformInterceptor.
    // Tự động unwrap để caller nhận `entity` trực tiếp, đồng thời map sang `INhomHang`.
    const wrapped = res as { data?: Record<string, unknown> } | Record<string, unknown>
    const raw = (wrapped as { data?: Record<string, unknown> }).data ?? (wrapped as Record<string, unknown>)
    return { data: mapDetail(raw) }
  })
}

export function capNhatNhomHang(id: NhomHangId, body: { ten: string; parentId?: NhomHangId | null }) {
  return apiRequest(
    ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-nhom-hang`, {
      ten: body.ten.trim(),
      ...(body.parentId != null && body.parentId !== '' ? { parentId: String(body.parentId) } : { parentId: null }),
    }),
  )
}

export function xoaNhomHang(id: NhomHangId) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-nhom-hang`))
}

export type XoaNhieuNhomHangResult = {
  ok: number
  failed: Array<{ id: string; message: string }>
}

/** Sắp xếp id theo cấp giảm dần — xóa lá / cấp sâu trước để tránh lỗi còn nhóm con. */
export const sortNhomHangIdsForDelete = (
  ids: Iterable<string>,
  items: Array<{ id: NhomHangId; cap?: number | null }>,
): string[] => {
  const capById = new Map(items.map((i) => [String(i.id), i.cap ?? 0]))
  return Array.from(ids).sort((a, b) => (capById.get(b) ?? 0) - (capById.get(a) ?? 0))
}

/** Xóa nhiều nhóm — gọi `DELETE .../xoa-nhom-hang` từng id (không có API bulk). */
export const xoaNhieuNhomHang = async (ids: NhomHangId[]): Promise<XoaNhieuNhomHangResult> => {
  const failed: XoaNhieuNhomHangResult['failed'] = []
  let ok = 0
  for (const id of ids) {
    try {
      await xoaNhomHang(id)
      ok += 1
    } catch (e) {
      failed.push({
        id: String(id),
        message: (e as { message?: string })?.message ?? '',
      })
    }
  }
  return { ok, failed }
}

export function chuyenTrangThaiNhomHang(id: NhomHangId, trangThai: NhomHangTrangThaiMa) {
  return apiRequest(
    ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-nhom-hang`, {
      trangThai,
    }),
  )
}

/** `CapNhatThuTuDto` — mỗi phần tử: id + thứ tự (1-based). */
export function capNhatThuTuNhomGoc(items: Array<{ id: NhomHangId; thuTu: number }>) {
  return apiRequest(
    ApisServiceApp.put(`${PREFIX}/cap-nhat-thu-tu-nhom-hang`, {
      danhSachId: items.map((x) => ({ id: String(x.id), thuTu: x.thuTu })),
    }),
  )
}

export type NhomHangImportResult = {
  success: boolean
  message: string
  created?: number
  updated?: number
  data?: unknown[]
}

export function nhapDanhSachNhomHang(rows: INhomHangImportRow[]) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/nhom-hang-nhap-danh-sach`, rows))
}

export function nhapNhomHangTuExcel(file: File) {
  return apiRequest(postMultipartFile(`${PREFIX}/nhap-nhom-hang-tu-excel`, file))
}

export type { NhomHangCayNode, NhomHangId } from '@/interfaces/nhomHangInterfaces'
